/**
 * PortalLayout — AppShell del portal de cliente.
 *
 * Sidebar fija (260px desktop / Sheet en mobile) con los 8 items del brief.
 * - Inicio (/sucursal-virtual)        - Reembolsos (/bonificaciones)
 * - Chat (/chat)                       - Blog (/blog)
 * - Invita y gana (/invita-y-gana)     - Beneficios (/beneficios)
 * - Cuotas (/suscripciones)            - Opciones (/opciones)
 *
 * CTA inferior "Seguir cotización" → /contratacion. Logout en footer.
 */

import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { useAuth } from '@modules/auth/presentation/hooks/useAuth';
import { useUnreadCount } from '@modules/notifications/presentation/hooks/useNotifications';
import { cn } from '@shared/utils/cn';
import {
  Home,
  ClipboardList,
  MessageCircle,
  BookOpen,
  Share2,
  BadgePercent,
  CreditCard,
  Settings,
  ArrowRight,
  LogOut,
  PawPrint,
  Bell,
} from 'lucide-react';

const navItems = [
  { to: '/sucursal-virtual', end: true, label: 'Inicio', icon: Home },
  { to: '/bonificaciones', end: false, label: 'Reembolsos', icon: ClipboardList },
  { to: '/chat', end: false, label: 'Chat', icon: MessageCircle },
  { to: '/blog', end: false, label: 'Blog', icon: BookOpen },
  { to: '/invita-y-gana', end: false, label: '¡Invita y gana!', icon: Share2 },
  { to: '/beneficios', end: false, label: 'Beneficios', icon: BadgePercent },
  { to: '/suscripciones', end: false, label: 'Cuotas', icon: CreditCard },
  { to: '/opciones', end: false, label: 'Opciones', icon: Settings },
];

function isItemActive(pathname: string, to: string, end: boolean) {
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

function PortalSidebar() {
  const { logout } = useAuth();
  const { pathname } = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <Link
          to="/sucursal-virtual"
          onClick={closeOnMobile}
          className="flex items-center gap-2 px-2 py-2"
          aria-label="Ir al inicio del portal"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <PawPrint className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="text-lg font-bold tracking-tight text-foreground group-data-[collapsible=icon]:hidden">
            mishiwoof
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {navItems.map(({ to, end, label, icon: Icon }) => {
                const active = isItemActive(pathname, to, end);
                return (
                  <SidebarMenuItem key={to}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={label}
                      className={cn(
                        'h-10 rounded-lg px-3 text-sm font-medium',
                        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                        'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-semibold',
                      )}
                    >
                      <NavLink to={to} end={end} onClick={closeOnMobile}>
                        <Icon strokeWidth={1.75} aria-hidden="true" />
                        <span>{label}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="gap-3">
        <Button
          asChild
          className="h-11 w-full rounded-full bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 group-data-[collapsible=icon]:hidden"
        >
          <Link to="/contratacion" onClick={closeOnMobile}>
            Seguir cotización
            <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Cerrar sesión"
              onClick={() => {
                closeOnMobile();
                logout();
              }}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut strokeWidth={1.75} aria-hidden="true" />
              <span>Cerrar sesión</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function PortalTopbar() {
  const { pathname } = useLocation();
  const current = navItems.find((item) => isItemActive(pathname, item.to, item.end));
  const title = current?.label ?? 'Portal';
  const { data: unreadData } = useUnreadCount();
  const unreadCount = (unreadData as { count?: number } | undefined)?.count ?? 0;

  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-6">
      <SidebarTrigger className="-ml-1" />
      <h1 className="truncate text-sm font-semibold text-foreground sm:text-base">
        {title}
      </h1>
      <div className="ml-auto flex items-center gap-2">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 rounded-full p-0"
          aria-label="Ver notificaciones"
        >
          <Link to="/notificaciones">
            <Bell className="h-4 w-4" aria-hidden="true" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Link>
        </Button>
      </div>
    </header>
  );
}

export function PortalLayout() {
  return (
    <SidebarProvider>
      <PortalSidebar />
      <SidebarInset className="bg-background">
        <PortalTopbar />
        <div className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
